/**
 * `/research` — the CaseBlink immigration library, for the attorney to search
 * directly: decisions, the statute and regulations, the manuals and the
 * newest policy updates. Every hit says what it is worth, a citation goes
 * straight to its document, and the reader opens a document whole beside
 * the results. Where the view stands (query, source, filters, open document)
 * lives in the URL, so Back and a shared link land in the same place.
 */
import { Search, X } from "lucide-react";
import { useEffect, useRef, useState, type FC, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { FilterChip } from "@/components/ui/filter-chip";
import { Kbd } from "@/components/ui/kbd";
import { PageHeader } from "@/components/ui/page-header";
import { cn } from "@/lib/utils";

import { AllSourcesResults } from "../components/AllSourcesResults";
import { CitationMatch } from "../components/CitationMatch";
import { DocumentReader } from "../components/DocumentReader";
import { ResearchLanding } from "../components/ResearchLanding";
import { SourceFilters } from "../components/SourceFilters";
import { SourceResults } from "../components/SourceResults";
import {
  parseResearchView,
  researchPath,
  type ResearchFilters,
  type ResearchView
} from "../routes";
import { RESEARCH_SOURCES, sourceById } from "../sources";
import type { LibraryHit, ResearchSourceId } from "../types";

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

export const ResearchPage: FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const view = parseResearchView(`?${params.toString()}`);
  const [draft, setDraft] = useState(view.query);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => setDraft(view.query), [view.query]);
  // "/" jumps to the search box, as in most search tools.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "/" || isTyping(e.target)) return;
      e.preventDefault();
      input.current?.focus();
      input.current?.select();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (next: Partial<ResearchView>, replace = false) =>
    navigate(researchPath({ ...view, ...next }), { replace });
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    go({ query: draft.trim(), docId: null });
  };
  const pickSource = (source: ResearchSourceId | null) =>
    go({ source, filters: {}, docId: null });
  const open = (hit: Pick<LibraryHit, "id">) =>
    hit.id && go({ docId: hit.id }, Boolean(view.docId));
  const openId = (id: string) => go({ docId: id }, Boolean(view.docId));
  const setFilters = (filters: ResearchFilters) =>
    go({ filters, docId: null }, true);

  const { source } = view;
  const browsing = source ? Boolean(sourceById(source).browsable) : false;
  const searching = view.query.length > 0 || browsing;
  const reading = Boolean(view.docId);

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col bg-surface"
      data-testid="research-page"
    >
      <div className="flex shrink-0 flex-col gap-3 px-6 pt-6 pb-3">
        <PageHeader
          title="Research"
          description="The CaseBlink immigration library: decisions, the INA and C.F.R., the Policy Manual and practice manuals, and the latest policy updates."
          className="w-full"
        />
        <form onSubmit={submit} className="relative" role="search">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-3" />
          <input
            ref={input}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={!view.query && !reading}
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") e.currentTarget.blur();
            }}
            placeholder={
              source
                ? `Search ${sourceById(source).label}, or paste a citation`
                : "Search decisions, statutes and manuals, or paste a citation like 26 I&N Dec. 884"
            }
            aria-label="Search the Library"
            data-testid="research-search"
            className="h-10 w-full rounded-xl bg-elevated pr-20 pl-9 text-md text-fg ring-1 ring-line-3 outline-none placeholder:text-fg-4 focus-visible:ring-2 focus-visible:ring-primary/40"
          />
          <div className="absolute top-1/2 right-3 flex -translate-y-1/2 items-center gap-1.5">
            {draft ? (
              <button
                type="button"
                aria-label="Clear Search"
                onClick={() => {
                  setDraft("");
                  go({ query: "", docId: null });
                  input.current?.focus();
                }}
                className="flex size-5 items-center justify-center rounded-full text-fg-3 hover:bg-fill-2 hover:text-fg"
              >
                <X className="size-3.5" />
              </button>
            ) : (
              <Kbd>/</Kbd>
            )}
          </div>
        </form>
        <div
          className="flex flex-wrap items-center gap-1.5"
          role="group"
          aria-label="Source"
        >
          <FilterChip
            active={source === null}
            onClick={() => pickSource(null)}
            data-testid="research-source-all"
          >
            All Sources
          </FilterChip>
          {RESEARCH_SOURCES.map((s) => (
            <FilterChip
              key={s.id}
              active={source === s.id}
              onClick={() => pickSource(s.id)}
              data-testid={`research-source-${s.id}`}
            >
              {s.label}
            </FilterChip>
          ))}
          {source ? (
            <div className="ml-auto flex items-center gap-1.5">
              <SourceFilters
                source={source}
                filters={view.filters}
                onChange={setFilters}
              />
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 border-t border-line-2">
        <div
          className={cn(
            "min-h-0 overflow-y-auto",
            reading ? "w-110 shrink-0 border-r border-line-2" : "flex-1"
          )}
          data-testid="research-results-pane"
        >
          {!searching ? (
            <ResearchLanding
              onSearch={(query) => go({ query, docId: null })}
              onOpen={open}
              activeId={view.docId}
            />
          ) : (
            <div
              className={cn(
                "flex flex-col pb-12",
                !reading && "mx-auto w-full max-w-3xl"
              )}
            >
              {view.query ? (
                <CitationMatch query={view.query} onOpen={openId} />
              ) : null}
              {source ? (
                <SourceResults
                  key={`${source}:${view.query}:${JSON.stringify(
                    view.filters
                  )}`}
                  source={source}
                  query={view.query}
                  filters={view.filters}
                  activeId={view.docId}
                  onOpen={open}
                />
              ) : (
                <AllSourcesResults
                  query={view.query}
                  activeId={view.docId}
                  onOpen={open}
                  onSeeAll={pickSource}
                />
              )}
            </div>
          )}
        </div>
        {view.docId ? (
          <div className="min-w-0 flex-1 bg-elevated">
            <DocumentReader
              docId={view.docId}
              query={view.query}
              onClose={() => go({ docId: null })}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};
